import React, { createContext, useState, useContext, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import cartService from "../services/cartService";
import { useAuth } from "./AuthContext";
import productService from "../services/productService";

const CartContext = createContext(null);

// Helper function to get cart from local storage
const getGuestCartFromLocalStorage = () => {
  const guestCart = localStorage.getItem("guestCart");
  try {
    return guestCart ? JSON.parse(guestCart) : { items: [], totalAmount: 0 };
  } catch (error) {
    console.error("Error parsing guest cart from local storage:", error);
    return { items: [], totalAmount: 0 };
  }
};

// Helper function to save cart to local storage
const saveGuestCartToLocalStorage = (cart) => {
  try {
    localStorage.setItem("guestCart", JSON.stringify(cart));
  } catch (error) {
    console.error("Error saving guest cart to local storage:", error);
  }
};

// Helper function to remove cart from local storage
const removeGuestCartFromLocalStorage = () => {
  localStorage.removeItem("guestCart");
};

export const CartProvider = ({ children }) => {
  const [cart, setCart] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const { isAuthenticated } = useAuth();
  const navigate = useNavigate();

  // Misafir sepetindeki ürünlerin fiyatlarını ve diğer detaylarını API'den güncelleyen fonksiyon
  const refreshGuestCartItemsPrices = async (guestCartToRefresh) => {
    if (
      !guestCartToRefresh ||
      !guestCartToRefresh.items ||
      guestCartToRefresh.items.length === 0
    ) {
      return guestCartToRefresh;
    }
    setLoading(true); // Fiyat güncelleme işlemi için yükleme durumunu başlat
    try {
      const updatedItems = await Promise.all(
        guestCartToRefresh.items.map(async (item) => {
          try {
            const productDetailsResponse = await productService.getProductById(
              item.productId
            );
            if (
              productDetailsResponse.succeeded &&
              productDetailsResponse.product
            ) {
              const productData = productDetailsResponse.product;
              return {
                ...item, // Mevcut miktar, not, misafire özel id gibi bilgileri koru
                price: productData.price,
                discountedPrice: productData.discountedPrice,
                productName: productData.name,
                productImageUrl: productData.imageUrl, // veya productData.images[0].url gibi bir alan
                // weight alanını weightGrams veya weightInGrams'dan hesapla (geriye dönük uyumluluk)
                weight: (item.weightGrams || item.weightInGrams) 
                  ? (item.weightGrams || item.weightInGrams) / 1000 
                  : (productData.weight || 0),
                // Ürün tipini güncelle (eğer yoksa)
                saleType: item.saleType !== undefined ? item.saleType : productData.saleType,
                minimumOrderWeight: item.minimumOrderWeight !== undefined ? item.minimumOrderWeight : productData.minimumOrderWeight
              };
            } else {
              console.warn(
                `Misafir sepetindeki ${item.productId} ID'li ürün için detaylar güncellenemedi: ${productDetailsResponse.message}`
              );
              return item; // API'den cevap alınamazsa mevcut item'ı döndür
            }
          } catch (err) {
            console.error(
              `Misafir sepetindeki ${item.productId} ID'li ürün için detaylar güncellenirken hata:`,
              err
            );
            return item; // Hata durumunda mevcut item'ı döndür
          }
        })
      );
      const newTotalAmount = calculateGuestCartTotal(updatedItems);
      return {
        ...guestCartToRefresh,
        items: updatedItems,
        totalAmount: newTotalAmount,
      };
    } catch (overallError) {
      console.error(
        "Misafir sepeti fiyatları güncellenirken genel bir hata oluştu:",
        overallError
      );
      setError("Sepet fiyatları güncellenirken bir sorun oluştu.");
      return guestCartToRefresh; // Hata durumunda orijinal sepeti döndür
    } finally {
      // setLoading(false); // Yükleme durumu useEffect içindeki ana loadCart fonksiyonunda yönetilecek
    }
  };

  useEffect(() => {
    const loadCart = async () => {
      setLoading(true);
      setError(null); // Hata state'ini de temizle
      
      if (isAuthenticated()) {
        const guestCart = getGuestCartFromLocalStorage();
        if (guestCart && guestCart.items && guestCart.items.length > 0) {
          try {
            // 1. Guest cart'ı senkronize et (ardışık bekleme)
            await syncGuestCartWithServer(guestCart);
            // 2. Local storage'dan guest cart'ı temizle
            removeGuestCartFromLocalStorage();
            // 3. Backend'den güncel cart'ı çek (GUID ID'lerle)
            await fetchCart();
          } catch (syncError) {
            console.error("Guest cart senkronizasyonu hatası:", syncError);
            setError("Sepetiniz yüklenirken bir sorun oluştu.");
            // Hata durumunda guest cart'ı temizle
            removeGuestCartFromLocalStorage();
            // Boş cart oluştur
            setCart({ items: [], totalAmount: 0, customPackages: [] });
          }
        } else {
          // Guest cart yoksa direkt backend'den çek
          await fetchCart();
        }
      } else {
        // Giriş yapmamış kullanıcı - guest cart yükle
        const guestCartData = getGuestCartFromLocalStorage();
        if (
          guestCartData &&
          guestCartData.items &&
          guestCartData.items.length > 0
        ) {
          const refreshedCart = await refreshGuestCartItemsPrices(
            guestCartData
          );
          setCart(refreshedCart);
          saveGuestCartToLocalStorage(refreshedCart);
        } else {
          setCart(guestCartData || { items: [], totalAmount: 0, customPackages: [] });
        }
      }
      
      setLoading(false);
    };
    loadCart();
  }, [isAuthenticated]); // Bağımlılıklar aynı kalır

  const syncGuestCartWithServer = async (guestCart) => {
    if (!guestCart || !guestCart.items || guestCart.items.length === 0) {
      return;
    }
    
    try {
      // Her ürünü backend'e ekle
      for (const guestItem of guestCart.items) {
        const weightGrams = guestItem.weightGrams || guestItem.weightInGrams;
        
        await cartService.addItemToCart({
          productId: guestItem.productId,
          quantity: guestItem.quantity,
          note: guestItem.note || "",
          weightGrams: weightGrams
        });
      }
    } catch (err) {
      console.error("Guest cart senkronizasyonu hatası:", err);
      throw err; // Hatayı üst katmana ilet
    }
  };

  const fetchCart = async () => {
    if (!isAuthenticated()) return;

    setLoading(true);
    setError(null);
    try {
      const response = await cartService.getUserCart();
      if (response.succeeded) {
        // API'den gelen cart bilgisini ayarla
        setCart(response.cart);
      } else {
        // Sepet bulunamadı, ancak API artık otomatik olarak yeni sepet oluşturuyor olmalı
        console.warn(
          "Sepet yanıtı başarılı değil ancak otomatik oluşturulmuş olmalı",
          response
        );
        setCart(response.cart || { items: [], totalAmount: 0 });
      }
    } catch (err) {
      setError("Sepet yüklenirken bir hata oluştu.");
      console.error("Sepet yükleme hatası:", err);
    } finally {
      setLoading(false);
    }
  };

  const addToCart = async (productId, quantity = 1, note = "", weightGrams) => {
    setLoading(true);
    setError(null);

    try {
      if (!isAuthenticated()) {
        // GİRİŞ YAPMAMIŞ KULLANICI - Misafir sepeti
        const productDetailsResponse = await productService.getProductById(productId);
        if (!productDetailsResponse.succeeded || !productDetailsResponse.product) {
          throw new Error(productDetailsResponse.message || "Ürün detayları alınamadı.");
        }
        const productData = productDetailsResponse.product;

        const currentGuestCart = getGuestCartFromLocalStorage();
        const existingItemIndex = currentGuestCart.items.findIndex(
          (item) => item.productId === productId
        );

        let updatedGuestCart;
        if (existingItemIndex > -1) {
          // Ürün zaten misafir sepetinde var, miktarını güncelle
          const updatedItems = currentGuestCart.items.map((item, index) =>
            index === existingItemIndex
              ? {
                  ...item,
                  quantity: item.quantity + quantity,
                  note: note || item.note,
                }
              : item
          );
          updatedGuestCart = { ...currentGuestCart, items: updatedItems };
        } else {
          // Ürünü misafir sepetine ekle
          const unitWeight = weightGrams || 1000; // gram cinsinden varsayılan ağırlık
          const newItem = {
            productId: productData.id,
            quantity,
            note,
            id: `guest_${productData.id}_${unitWeight}_${Date.now()}`,
            price: productData.price, // Kg başına fiyat
            discountedPrice: productData.discountedPrice, // İndirimli kg başına fiyat
            productName: productData.name,
            productImageUrl: productData.imageUrl,
            weightGrams: unitWeight,
            saleType: productData.saleType, // Ürün tipini kaydet (0: Adet, 1: Kg)
            minimumOrderWeight: productData.minimumOrderWeight // Minimum sipariş ağırlığı
          };
          updatedGuestCart = {
            ...currentGuestCart,
            items: [...currentGuestCart.items, newItem],
          };
        }
        updatedGuestCart.totalAmount = calculateGuestCartTotal(updatedGuestCart.items);

        setCart(updatedGuestCart);
        saveGuestCartToLocalStorage(updatedGuestCart);
        return { success: true, message: "Ürün misafir sepetine eklendi." };
      } else {
        // GİRİŞ YAPMIŞ KULLANICI - API kullan
        const payload = { productId, quantity, note };
        if (weightGrams) payload.weightGrams = weightGrams;
        const response = await cartService.addItemToCart(payload);

        if (response.succeeded) {
          setCart(response.cart);
          return { success: true };
        } else {
          setError(response.errorMessage || "Ürün sepete eklenirken bir hata oluştu.");
          return { success: false, message: response.errorMessage };
        }
      }
    } catch (err) {
      const errorMsg = err.message || "Ürün sepete eklenirken bir hata oluştu.";
      setError(errorMsg);
      console.error("Sepete ekleme hatası:", err);
      return { success: false, message: errorMsg };
    } finally {
      setLoading(false);
    }
  };

  // Özelleştirilmiş paket ekleme fonksiyonu
  const addCustomPackageToCart = async (items, products = []) => {
    if (!isAuthenticated()) {
      // Misafir kullanıcı için yerel depolama kullan
      setLoading(true);
      setError(null);
      try {
        const currentGuestCart = getGuestCartFromLocalStorage();
        
        // Özelleştirilmiş paket için benzersiz ID oluştur
        const customPackageId = `guest_custom_${Date.now()}`;
        
        // Özelleştirilmiş paket öğelerini oluştur
        const customPackageItems = items.map(item => {
          // Ürün ismini bul
          const product = products.find(p => p.id === item.productId);
          return {
            productId: item.productId,
            quantity: item.quantity,
            weightGrams: item.weightGrams,
            unitPrice: item.unitPrice,
            totalPrice: item.unitPrice * item.quantity, // Toplam fiyat hesapla
            id: `guest_custom_item_${item.productId}_${Date.now()}`,
            productName: product ? product.name : 'Bilinmeyen Ürün',
            productImageUrl: product ? product.imageUrl : ''
          };
        });

        // Özelleştirilmiş paketi misafir sepetine ekle
        const customPackage = {
          id: customPackageId,
          items: customPackageItems,
          totalAmount: items.reduce((sum, item) => sum + (item.unitPrice * item.quantity), 0),
          type: 'customPackage'
        };

        // Misafir sepetine özelleştirilmiş paketleri ekle (eğer yoksa oluştur)
        if (!currentGuestCart.customPackages) {
          currentGuestCart.customPackages = [];
        }
        
        currentGuestCart.customPackages.push(customPackage);
        
        // Toplam tutarı güncelle
        const itemsTotal = calculateGuestCartTotal(currentGuestCart.items);
        const packagesTotal = currentGuestCart.customPackages.reduce((sum, pkg) => 
          sum + pkg.items.reduce((itemSum, item) => itemSum + (item.totalPrice || (item.unitPrice * item.quantity)), 0), 0);
        currentGuestCart.totalAmount = itemsTotal + packagesTotal;

        setCart(currentGuestCart);
        saveGuestCartToLocalStorage(currentGuestCart);
        return { success: true, message: "Özelleştirilmiş paket misafir sepetine eklendi." };
      } catch (err) {
        const errorMsg = "Özelleştirilmiş paket misafir sepetine eklenirken bir hata oluştu.";
        setError(errorMsg);
        console.error("Misafir sepeti hatası:", err);
        return { success: false, message: errorMsg };
      } finally {
        setLoading(false);
      }
    } else {
      // Giriş yapmış kullanıcı için API kullan
      setLoading(true);
      setError(null);
      try {
        const result = await cartService.addCustomPackage({ items });
        if (result.succeeded) {
          await fetchCart();
          return { success: true, message: "Özelleştirilmiş paket sepete eklendi." };
        } else {
          setError(result.errorMessage || "Özelleştirilmiş paket sepete eklenemedi.");
          return { success: false, message: result.errorMessage || "Özelleştirilmiş paket sepete eklenemedi." };
        }
      } catch (err) {
        const errorMsg = "Özelleştirilmiş paket sepete eklenirken bir hata oluştu.";
        setError(errorMsg);
        console.error("Sepet hatası:", err);
        return { success: false, message: errorMsg };
      } finally {
        setLoading(false);
      }
    }
  };

  const updateCartItem = async (cartItemIdOrProductId, quantity, note, weightGrams) => {
    setLoading(true);
    setError(null);

    try {
      if (!isAuthenticated()) {
        // GİRİŞ YAPMAMIŞ KULLANICI - Misafir sepeti
        const currentGuestCart = getGuestCartFromLocalStorage();
        
        const updatedItems = currentGuestCart.items
          .map((item) => {
            if (item.productId === cartItemIdOrProductId || item.id === cartItemIdOrProductId) {
              const updatedItem = { ...item, quantity: quantity, note: note };
              if (weightGrams !== undefined && weightGrams !== null) {
                updatedItem.weightGrams = weightGrams;
              }
              return updatedItem;
            }
            return item;
          })
          .filter((item) => item.quantity > 0);

        let updatedCustomPackages = currentGuestCart.customPackages || [];

        const updatedGuestCart = { 
          ...currentGuestCart, 
          items: updatedItems,
          customPackages: updatedCustomPackages
        };
        
        updatedGuestCart.totalAmount = calculateGuestCartTotal(updatedItems);

        setCart(updatedGuestCart);
        saveGuestCartToLocalStorage(updatedGuestCart);
        return { success: true, message: "Misafir sepeti güncellendi." };
      } else {
        // GİRİŞ YAPMIŞ KULLANICI - API kullan
        if (!cart || !cart.items) {
          setError("Sepet yükleniyor, lütfen bekleyin.");
          return { success: false, message: "Sepet yükleniyor, lütfen bekleyin." };
        }
        
        // Guest ID kontrolü ekle
        if (typeof cartItemIdOrProductId === "string" && 
            cartItemIdOrProductId.startsWith("guest_")) {
          // Guest ID algılandı - cart'ı yeniden yükle
          console.warn("Guest ID algılandı, cart yeniden yükleniyor...");
          await fetchCart();
          setError("Sepet güncellendi, lütfen tekrar deneyin.");
          return { success: false, message: "Sepet güncellendi, lütfen tekrar deneyin." };
        }
        
        let actualCartItemId = cartItemIdOrProductId;
        if (
          typeof cartItemIdOrProductId === "string" &&
          !cart.items.find((item) => item.id === cartItemIdOrProductId)
        ) {
          const targetItem = cart.items.find(
            (item) => item.productId === cartItemIdOrProductId
          );
          if (targetItem) {
            actualCartItemId = targetItem.id;
          } else {
            setError("Güncellenecek ürün sepette bulunamadı.");
            return {
              success: false,
              message: "Güncellenecek ürün sepette bulunamadı.",
            };
          }
        }

        const response = await cartService.updateCartItem({
          cartItemId: actualCartItemId,
          quantity,
          note,
          weightGrams,
        });

        if (response.succeeded) {
          setCart(response.cart);
          return { success: true };
        } else {
          setError(response.errorMessage || "Sepet güncellenirken bir hata oluştu.");
          return { success: false, message: response.errorMessage };
        }
      }
    } catch (err) {
      const errorMsg = "Sepet güncellenirken bir hata oluştu.";
      setError(errorMsg);
      console.error("Sepet güncelleme hatası:", err);
      return { success: false, message: errorMsg };
    } finally {
      setLoading(false);
    }
  };

  const removeCartItem = async (cartItemIdOrProductId) => {
    setLoading(true);
    setError(null);

    if (!isAuthenticated()) {
      // Misafir kullanıcı işlemleri
      try {
        const currentGuestCart = getGuestCartFromLocalStorage();
        
        // Önce normal sepet öğelerini kontrol et
        const updatedItems = currentGuestCart.items.filter(
          (item) => item.productId !== cartItemIdOrProductId && item.id !== cartItemIdOrProductId
        );
        
        // Özelleştirilmiş paketleri kontrol et
        let updatedCustomPackages = currentGuestCart.customPackages || [];
        if (currentGuestCart.customPackages) {
          updatedCustomPackages = currentGuestCart.customPackages.filter(
            (pkg) => pkg.id !== cartItemIdOrProductId
          );
        }
        
        const updatedGuestCart = { 
          ...currentGuestCart, 
          items: updatedItems,
          customPackages: updatedCustomPackages
        };
        
        // Toplam tutarı güncelle
        const itemsTotal = calculateGuestCartTotal(updatedItems);
        const packagesTotal = updatedCustomPackages.reduce((sum, pkg) => 
          sum + pkg.items.reduce((itemSum, item) => itemSum + (item.totalPrice || (item.unitPrice * item.quantity)), 0), 0);
        updatedGuestCart.totalAmount = itemsTotal + packagesTotal;

        setCart(updatedGuestCart);
        saveGuestCartToLocalStorage(updatedGuestCart);
        return { success: true, message: "Ürün misafir sepetinden çıkarıldı." };
      } catch (err) {
        const errorMsg = "Ürün misafir sepetinden çıkarılırken hata oluştu.";
        setError(errorMsg);
        console.error("Misafir sepetinden ürün çıkarma hatası:", err);
        return { success: false, message: errorMsg };
      } finally {
        setLoading(false);
      }
    } else {
      // Giriş yapmış kullanıcı işlemleri
      if (!cart) {
        setLoading(false);
        setError("Sepet yükleniyor, lütfen bekleyin.");
        return { success: false, message: "Sepet yükleniyor, lütfen bekleyin." };
      }
      
      // Guest ID kontrolü
      if (typeof cartItemIdOrProductId === "string" && 
          cartItemIdOrProductId.startsWith("guest_")) {
        console.warn("Guest ID algılandı, cart yeniden yükleniyor...");
        await fetchCart();
        setLoading(false);
        setError("Sepet güncellendi, lütfen tekrar deneyin.");
        return { success: false, message: "Sepet güncellendi, lütfen tekrar deneyin." };
      }
      
      try {
        // Önce özelleştirilmiş paketleri kontrol et
        if (cart.customPackages && cart.customPackages.some(pkg => pkg.id === cartItemIdOrProductId)) {
          const response = await cartService.removeCustomPackage(cartItemIdOrProductId);
          if (response.succeeded) {
            setCart(response.cart);
            return { success: true };
          } else {
            setError(response.errorMessage || "Özelleştirilmiş paket sepetten çıkarılırken bir hata oluştu.");
            return { success: false, message: response.errorMessage };
          }
        }

        // Normal sepet öğelerini kontrol et
        let actualCartItemId = cartItemIdOrProductId;
        if (
          typeof cartItemIdOrProductId === "string" &&
          !cart.items.find((item) => item.id === cartItemIdOrProductId)
        ) {
          const targetItem = cart.items.find(
            (item) => item.productId === cartItemIdOrProductId
          );
          if (targetItem) {
            actualCartItemId = targetItem.id;
          } else {
            setLoading(false);
            setError("Silinecek ürün sepette bulunamadı.");
            return {
              success: false,
              message: "Silinecek ürün sepette bulunamadı.",
            };
          }
        }

        const response = await cartService.removeCartItem(actualCartItemId);

        if (response.succeeded) {
          setCart(response.cart);
          return { success: true };
        } else {
          setError(
            response.errorMessage ||
              "Ürün sepetten çıkarılırken bir hata oluştu."
          );
          return { success: false, message: response.errorMessage };
        }
      } catch (err) {
        const errorMsg = "Ürün sepetten çıkarılırken bir hata oluştu.";
        setError(errorMsg);
        console.error("Sepetten ürün çıkarma hatası:", err);
        return { success: false, message: errorMsg };
      } finally {
        setLoading(false);
      }
    }
  };

  const clearCart = async () => {
    setLoading(true);
    setError(null);

    if (!isAuthenticated()) {
      // Misafir kullanıcı işlemleri
      try {
        const clearedCart = { items: [], customPackages: [], totalAmount: 0 };
        setCart(clearedCart);
        saveGuestCartToLocalStorage(clearedCart);
        return { success: true, message: "Misafir sepeti temizlendi." };
      } catch (err) {
        const errorMsg = "Misafir sepeti temizlenirken bir hata oluştu.";
        setError(errorMsg);
        console.error("Misafir sepeti temizleme hatası:", err);
        return { success: false, message: errorMsg };
      } finally {
        setLoading(false);
      }
    } else {
      // Giriş yapmış kullanıcı işlemleri
      try {
        const response = await cartService.clearCart();

        if (response.succeeded) {
          setCart({ items: [], customPackages: [], totalAmount: 0 });
          return { success: true };
        } else {
          setError(
            response.errorMessage || "Sepet temizlenirken bir hata oluştu."
          );
          return { success: false, message: response.errorMessage };
        }
      } catch (err) {
        const errorMsg = "Sepet temizlenirken bir hata oluştu.";
        setError(errorMsg);
        console.error("Sepet temizleme hatası:", err);
        return { success: false, message: errorMsg };
      } finally {
        setLoading(false);
      }
    }
  };

  const getCartTotalItems = () => {
    if (!cart) return 0;
    const itemsCount = cart.items
      ? cart.items.reduce((total, item) => total + (item.quantity || 0), 0)
      : 0;
    const packagesCount = cart.customPackages ? cart.customPackages.length : 0;
    return itemsCount + packagesCount;
  };

  const getCartTotalPrice = () => {
    if (!cart) return 0;
    // Sum normal cart items - doğru hesaplama yap
    const itemsTotal = (cart.items || []).reduce((total, item) => {
      const price = item.product?.price || item.price || 0;
      const discountedPrice = item.product?.discountedPrice ?? item.discountedPrice;
      const effectivePrice = discountedPrice != null ? discountedPrice : price;
      
      // SaleType kontrolü
      const saleType = item.saleType !== undefined ? item.saleType : (item.weightGrams || item.weightInGrams ? 1 : 0);
      
      if (saleType === 1) {
        // Kilogram bazlı ürün - weightInGrams'ı kg'a çevir ve fiyatla çarp
        // Kilogram bazlıda quantity her zaman 1'dir, ağırlık weightInGrams'da
        const weightInGrams = item.weightInGrams || item.weightGrams || 1000;
        const weightInKg = weightInGrams / 1000;
        return total + effectivePrice * weightInKg;
      } else {
        // Adet bazlı ürün - quantity ile çarp
        const quantity = item.quantity || 0;
        return total + effectivePrice * quantity;
      }
    }, 0);
    // Sum custom package totals
    const packagesTotal = (cart.customPackages || []).reduce(
      (sum, pkg) => sum + (pkg.totalAmount || pkg.items.reduce((itemSum, item) => itemSum + (item.totalPrice || (item.unitPrice * item.quantity)), 0)),
      0
    );
    return itemsTotal + packagesTotal;
  };

  // Helper function to calculate guest cart total (assuming items have price and quantity)
  // Bu fonksiyonu getCartTotalPrice ile birleştirebilir veya ona benzer yapabiliriz.
  // Şimdilik misafir sepetindeki ürünlerin fiyat bilgisine sahip olmadığını varsayarak
  // toplamı 0 döndüreceğiz. Bu, ürün detayları misafir sepetine eklendiğinde güncellenmeli.
  const calculateGuestCartTotal = (items) => {
    if (!items || items.length === 0) return 0;
    return items.reduce((total, item) => {
      // Her ürün için indirimli fiyat varsa onu, yoksa normal fiyatı kullan
      const effectivePrice =
        item.discountedPrice !== undefined && item.discountedPrice !== null
          ? item.discountedPrice
          : item.price || 0;
      
      // SaleType kontrolü
      const saleType = item.saleType !== undefined ? item.saleType : (item.weightGrams || item.weightInGrams ? 1 : 0);
      
      if (saleType === 1) {
        // Kilogram bazlı ürün - weightInGrams'ı kg'a çevir ve fiyatla çarp
        // Kilogram bazlıda quantity her zaman 1'dir, ağırlık weightInGrams'da
        const weightInGrams = item.weightInGrams || item.weightGrams || 1000;
        const weightInKg = weightInGrams / 1000;
        return total + effectivePrice * weightInKg;
      } else {
        // Adet bazlı ürün - quantity ile çarp
        const quantity = item.quantity || 0;
        return total + effectivePrice * quantity;
      }
    }, 0);
  };

  const value = {
    cart,
    loading,
    error,
    fetchCart,
    addToCart,
    addCustomPackageToCart,
    updateCartItem,
    removeCartItem,
    clearCart,
    getCartTotalItems,
    getCartTotalPrice,
    // refreshGuestCartItemsPrices, // Eğer dışarıdan çağrılması gerekiyorsa context value'suna eklenebilir
  };

  return <CartContext.Provider value={value}>{children}</CartContext.Provider>;
};

export const useCart = () => {
  const context = useContext(CartContext);
  if (!context) {
    throw new Error("useCart must be used within a CartProvider");
  }
  return context;
};
